/**
 * ChatPanel - Shared chat UI for visitor + resident.
 * GameBoy edition: pixelated, 4-color palette, no rounded corners.
 *
 * - Polls /visits/rings/:id/messages?since=... every 3s (paused when tab
 *   is hidden via Page Visibility API — saves battery, avoids stale data)
 * - Renders messages with sender differentiation (mine = inverted/dark)
 * - Text input + audio recorder at the bottom
 * - Auto-scrolls to newest message
 * - Applies padding-bottom equal to keyboard height so the input stays
 *   visible above the iOS keyboard without scrolling the page
 * - Stops polling when ring is rejected/cancelled/timeout (calls onRingEnded)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { ChatMessage } from '@/components/ChatMessage';
import { AudioRecorder } from '@/components/AudioRecorder';
import {
  pollMessages,
  sendText,
  sendAudio,
  endRing,
  RingChatError,
  type ChatMessage as Msg,
  type Sender,
} from '@/lib/ringChat';
import { useKeyboardHeight } from '@/lib/useKeyboardHeight';

interface ChatPanelProps {
  ringId: string;
  mySide: Sender;
  /** When the ring status becomes rejected/cancelled/timeout, the parent renders its own UI. */
  onRingEnded: () => void;
  /** Optional "end conversation" button shown at the bottom. */
  showEndButton?: boolean;
  /** Optional status banner shown above the messages. */
  statusBanner?: React.ReactNode;
}

export function ChatPanel({
  ringId,
  mySide,
  onRingEnded,
  showEndButton = true,
  statusBanner,
}: ChatPanelProps) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastTsRef = useRef<string>('1970-01-01T00:00:00.000Z');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onRingEndedRef = useRef(onRingEnded);
  onRingEndedRef.current = onRingEnded;

  const keyboardHeight = useKeyboardHeight();

  // Poll loop — pauses on tab hide, resumes on tab show.
  useEffect(() => {
    let active = true;
    let intervalId: number | null = null;

    const poll = async () => {
      if (!active) return;
      try {
        const r = await pollMessages(ringId, lastTsRef.current);
        if (!active) return;
        if (r.messages.length > 0) {
          lastTsRef.current = r.messages[r.messages.length - 1].created_at;
          setMessages((prev) => {
            // de-dup by id
            const seen = new Set(prev.map((m) => m.id));
            return [...prev, ...r.messages.filter((m) => !seen.has(m.id))];
          });
        }
        // 'rejected' | 'cancelled' | 'timeout' all mean the chat is over.
        // 'ended' isn't a valid status (the worker writes 'cancelled' instead,
        // keeping ended_at as a timestamp). See api/migrations/0024_door_rings.sql.
        if (
          r.ring.status === 'rejected' ||
          r.ring.status === 'cancelled' ||
          r.ring.status === 'timeout'
        ) {
          onRingEndedRef.current();
        }
      } catch (err) {
        if (err instanceof RingChatError && err.status === 410) {
          // Ring expired or ended — drop out gracefully.
          onRingEndedRef.current();
          return;
        }
        if (!active) return;
        const fallback = 'Error de conexión';
        const msg = err instanceof RingChatError && err.message
          ? err.message
          : err instanceof Error
            ? err.message
            : fallback;
        setError(msg);
      }
    };

    const start = () => {
      if (intervalId !== null) return;
      poll(); // immediate fetch on (re)start
      intervalId = window.setInterval(poll, 3000);
    };
    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    start();
    const onVis = () => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      active = false;
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ringId]);

  // Auto-scroll on new message — skip smooth scroll when the keyboard
  // is open so it doesn't fight the user's typing position.
  useEffect(() => {
    // Use a local scroll on the messages log only (NOT scrollIntoView) to
    // avoid scrolling the page (or any ancestor with hidden overflow like
    // .gameboy-wrap), which would shift the shell up out of view.
    const log = messagesEndRef.current?.parentElement;
    if (log) log.scrollTop = log.scrollHeight;
  }, [messages.length, keyboardHeight]);

  const sendTextMsg = useCallback(async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setError(null);
    try {
      const m = await sendText(ringId, mySide, t);
      lastTsRef.current = m.created_at;
      setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      setText('');
    } catch (err) {
      const fallback = 'Error al enviar';
      const msg = err instanceof RingChatError && err.message
        ? err.message
        : err instanceof Error
          ? err.message
          : fallback;
      setError(msg);
    } finally {
      setSending(false);
    }
  }, [text, sending, ringId, mySide]);

  const sendAudioMsg = useCallback(
    async (base64: string, mime: string) => {
      setError(null);
      try {
      const m = await sendAudio(ringId, mySide, base64, mime);
      lastTsRef.current = m.created_at;
      setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    } catch (err) {
      const fallback = 'Error al enviar audio';
      const msg = err instanceof RingChatError && err.message
        ? err.message
        : err instanceof Error
          ? err.message
          : fallback;
      setError(msg);
    }
    },
    [ringId, mySide],
  );

  const handleAudioError = useCallback((msg: string) => {
    setError(msg);
  }, []);

  const handleEnd = useCallback(async () => {
    if (!window.confirm('¿Terminar la conversación?')) return;
    try {
      await endRing(ringId);
    } catch {
      // Even if the call fails, leave the screen
    }
    onRingEnded();
  }, [ringId, onRingEnded]);

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--gb-bg)',
      }}
    >
      {statusBanner && (
        <div style={{ padding: '4px 6px', borderBottom: '1px dashed var(--gb-mid)' }}>
          {statusBanner}
        </div>
      )}

      <div
        role="log"
        aria-live="polite"
        aria-label="Mensajes del chat"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '6px 6px 4px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          scrollbarWidth: 'thin',
          // Stack messages from the bottom (just above the input row) so
          // the latest message is always visible — matches the typical
          // chat-app UX where new messages appear from below.
          justifyContent: 'flex-end',
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              fontFamily: 'var(--pixel)',
              fontSize: 7,
              color: 'var(--gb-mid)',
              letterSpacing: 0.4,
              paddingBottom: 2,
              textAlign: 'center',
            }}
          >
            TODAVÍA NO HAY MENSAJES
          </div>
        )}
        {messages.map((m) => (
          <ChatMessage
            key={m.id}
            msg={m}
            ringId={ringId}
            side={m.sender === mySide ? 'right' : 'left'}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '2px 6px',
            fontFamily: 'var(--pixel)',
            fontSize: 6,
            color: 'var(--gb-bg)',
            background: 'var(--gb-deepest)',
            borderTop: '1px dashed var(--gb-mid)',
            letterSpacing: 0.4,
          }}
        >
          ! {error}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: 3,
          // Push the input row up by the keyboard height so the text input
          // stays visible above the native keyboard. This is in addition to
          // GameBoyShell's `.keyboard-open` mode which re-scales the shell.
          paddingBottom: 3 + keyboardHeight,
          borderTop: '2px solid var(--gb-dark)',
          background: 'var(--gb-bg)',
          // Smooth the transition when the keyboard slides in/out.
          transition: 'padding-bottom 0.2s ease-out',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          enterKeyHint="send"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="sentences"
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={(e) => {
            // Prevent iOS Safari from auto-scrolling the body to "show the
            // input above the keyboard". Without this, the .gameboy shell
            // jumps when the user taps the chat input.
            e.preventDefault();
            inputRef.current?.focus({ preventScroll: true });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendTextMsg();
            }
          }}
          placeholder="Escribí…"
          disabled={sending}
          maxLength={500}
          data-testid="chat-input"
          aria-label="Mensaje"
          style={{
            flex: 1,
            minWidth: 0,
            background: 'var(--gb-bg)',
            color: 'var(--gb-dark)',
            border: '2px solid var(--gb-dark)',
            borderRadius: 0,
            padding: '4px 6px',
            fontFamily: 'var(--pixel)',
            fontSize: 8,
            outline: 'none',
            // Don't add extra scroll offset when iOS focuses this input.
            scrollMarginTop: 0,
            scrollMarginBottom: 0,
          }}
        />
        <button
          type="button"
          onClick={sendTextMsg}
          disabled={!text.trim() || sending}
          data-testid="chat-send"
          aria-label="Enviar mensaje"
          style={{
            width: 22,
            height: 22,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--gb-dark)',
            color: 'var(--gb-bg)',
            border: '2px solid var(--gb-dark)',
            fontFamily: 'var(--pixel)',
            fontSize: 6,
            cursor: (!text.trim() || sending) ? 'not-allowed' : 'pointer',
            opacity: (!text.trim() || sending) ? 0.4 : 1,
            padding: 0,
            flexShrink: 0,
          }}
        >
          {sending ? '...' : '►'}
        </button>
        <AudioRecorder
          onSend={sendAudioMsg}
          onError={handleAudioError}
          disabled={sending}
        />
      </div>

      {showEndButton && (
        <div style={{ padding: '0 4px 3px' }}>
          <button
            type="button"
            onClick={handleEnd}
            data-testid="chat-end"
            aria-label="Terminar conversación"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              background: 'var(--gb-bg)',
              color: 'var(--gb-mid)',
              border: '1px dashed var(--gb-mid)',
              fontFamily: 'var(--pixel)',
              fontSize: 5,
              letterSpacing: 0.4,
              padding: '2px 6px',
              minHeight: 0,
              cursor: 'pointer',
            }}
          >
            X TERMINAR CONVERSACIÓN
          </button>
        </div>
      )}
    </div>
  );
}
